import posthog, { type PostHog } from 'posthog-js';

export default defineNuxtPlugin(() => {
  const runtimeConfig = useRuntimeConfig();
  const { cookieConsent } = useCookieConsent();
  const { data: session } = useAuth();
  let posthogInitialized = false;

  if (!runtimeConfig.public.posthogEnabled) {
    return {
      provide: {
        posthog: null as PostHog | null,
      },
    };
  }

  const isSuperAdmin = () => session.value?.roles?.includes('ROLE_SUPER_ADMIN') ?? false;

  watch(
    () => cookieConsent.value.thirdParty,
    (enabled) => {
      if (isSuperAdmin()) return;

      if (enabled && !posthogInitialized) {
        posthog.init(runtimeConfig.public.posthogPublicKey, {
          api_host: runtimeConfig.public.posthogHost,
          person_profiles: 'identified_only',
          capture_pageview: false,
          disable_session_recording: runtimeConfig.public.capacitor,
        });
        posthog.register({ app_version: runtimeConfig.public.appVersion });
        posthogInitialized = true;
      } else if (enabled && posthogInitialized) {
        posthog.opt_in_capturing();
      } else if (!enabled && posthogInitialized) {
        posthog.opt_out_capturing();
      }
    },
    { immediate: true, deep: true },
  );

  // Opt out when session changes to SuperAdmin (e.g. after login)
  watch(
    () => session.value?.roles,
    (roles) => {
      if (posthogInitialized && roles?.includes('ROLE_SUPER_ADMIN')) {
        posthog.opt_out_capturing();
      }
    },
  );

  // Capture pageviews on route change (only if capturing is active)
  const router = useRouter();
  router.afterEach((to) => {
    if (posthogInitialized && cookieConsent.value.thirdParty && !isSuperAdmin()) {
      posthog.capture('$pageview', {
        current_url: to.fullPath,
      });
    }
  });

  return {
    provide: {
      posthog: posthog as PostHog | null,
    },
  };
});
